<template>
  <div
    class="border-static-default-low mb-56 grid items-center border-b md:mb-0 md:h-96 md:gap-y-0"
    :class="gridClasses"
  >
    <!-- Grade -->
    <div :class="rowClasses">
      <p :class="headingClasses">
        {{ i18n(translations.grade) }}
      </p>
      <RevTooltip class="md:w-full" position="top-start">
        <template #trigger="{ show, hide }">
          <RevTag
            class="cursor-pointer"
            :label="getQuoteGradeName(listing.grade)"
            variant="secondary"
            @focusin="show"
            @focusout="hide"
            @mouseleave="hide"
            @mouseover="show"
          />
        </template>
        {{ getQuoteGradeName(listing.grade) }}
      </RevTooltip>
    </div>
    <!-- Price to win BackBox  -->
    <div :class="rowClasses">
      <div class="body-1-bold flex items-center" :class="headingClasses">
        <p class="text-balance">
          {{ i18n(translations.priceToWinBackboxTitle) }}
        </p>
        <RevTooltip position="bottom-end">
          <template #trigger="{ show, hide }">
            <RevButtonIcon
              class="cursor-pointer"
              :disabled="false"
              :icon="IconInfo"
              variant="secondary"
              @focusin="show"
              @focusout="hide"
              @mouseleave="hide"
              @mouseover="show"
            />
          </template>
          {{ i18n(translations.priceToWinBackboxTooltip) }}
        </RevTooltip>
      </div>
      <RevSpinner
        v-if="isLoading || isPriceToWinLoading"
        class="ml-auto md:mx-auto"
      ></RevSpinner>
      <p v-else>
        {{
          i18n.price(priceToWinBackbox, {
            trailingZeroDisplay: 'stripIfInteger',
          })
        }}
      </p>
    </div>
    <!-- Backbox Price -->
    <div :class="rowClasses">
      <p :class="headingClasses">
        {{ i18n(translations.backboxPrice) }}
      </p>
      <RevSpinner
        v-if="isLoading || isPriceToWinLoading"
        class="ml-auto md:mx-auto"
      ></RevSpinner>
      <p v-else-if="listing.backboxPrice" class="body-1">
        {{
          i18n.price(listing.backboxPrice, {
            trailingZeroDisplay: 'stripIfInteger',
          })
        }}
      </p>
    </div>
    <!-- Price -->
    <div :class="rowClasses">
      <p class="w-full" :class="headingClasses">
        {{ i18n(translations.yourPrice) }}
      </p>
      <RevSpinner v-if="isLoading" class="ml-auto md:mx-auto"></RevSpinner>
      <RevInputText
        v-else
        :id="`${listing.price}-price`"
        v-model="computedPrice.amount"
        class="w-full"
        data-test="listing-input-price"
        :format="(value) => removeNonDecimalCharacters(value as string)"
        :label="i18n(translations.priceInputLabel, { currency })"
        @update:model-value="handlePriceUpdate"
      />
    </div>
    <!-- SKU -->
    <div :class="rowClasses">
      <p class="w-full" :class="headingClasses">
        {{ i18n(translations.sku) }}
      </p>
      <RevSpinner v-if="isLoading" class="ml-auto md:mx-auto"></RevSpinner>
      <RevInputText
        v-else
        :id="`${listing.grade}-sku`"
        v-model="computedSku"
        class="w-full"
        :label="i18n(translations.sku)"
        type="text"
      />
    </div>
    <div class="grid h-96 items-center justify-self-end md:h-auto">
      <RevTooltip position="bottom-end">
        <template #trigger="{ show, hide }">
          <RevButton
            class="place-self-center"
            :disabled="isButtonDisabled"
            size="small"
            variant="primary"
            @click="emit('get-backbox-clicked')"
            @focusin="() => handleTooltipVisibility(show, hide)"
            @focusout="hide"
            @mouseleave="hide"
            @mouseover="() => handleTooltipVisibility(show, hide)"
          >
            {{ i18n(translations.getBackbox) }}
          </RevButton>
        </template>
        {{ i18n(translations.alreadyHaveBackbox) }}
      </RevTooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'

import { MarketCountryCode, type MonetaryAmount } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { getMarketplace } from '@backmarket/nuxt-module-marketplace/getMarketplace'
import { tw } from '@backmarket/utils/string/tw'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevInputText } from '@ds/components/InputText'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTag } from '@ds/components/Tag'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'
import type { InputValue } from '@ds/types/InputValue'

import type {
  BuyBackQuoteListing,
  BuyBackQuotePricingEstimation,
} from '~/api/quotes/types'
import { usePriceToWinBackbox } from '~/scopes/buyback/composables/usePriceToWinBackbox'
import translations from '~/scopes/buyback/pages/quotations/CreateProductQuotations.translations'
import { getQuoteGradeName } from '~/scopes/buyback/pages/wishlist/WishList.utils'
import { removeNonDecimalCharacters } from '~/scopes/buyback/utils/buyback.utils'

const props = withDefaults(
  defineProps<{
    listing: BuyBackQuoteListing
    marketCode: string
    isLoading: boolean
    isPriceToWinLoading?: boolean
    pricingEstimation?: BuyBackQuotePricingEstimation
    gridClasses: string
  }>(),
  {
    isPriceToWinLoading: false,
    pricingEstimation: undefined,
  },
)

const price = defineModel<MonetaryAmount | null>('price', { required: true })
const sku = defineModel<string | null>('sku', { required: true })

const emit = defineEmits(['get-backbox-clicked'])

const i18n = useI18n()
const rowClasses = tw`h-96 border-b border-static-default-low flex gap-x-16 items-center justify-between md:gap-x-0`
const headingClasses = tw`body-1-bold md:hidden`

const currency = computed(
  () =>
    getMarketplace(props.marketCode as MarketCountryCode)?.market
      ?.defaultCurrency,
)

const { pricingEstimation, listing } = toRefs(props)
const priceToWinBackbox = usePriceToWinBackbox(
  pricingEstimation,
  listing,
  currency,
)

// Computed property to handle null to object conversion to match RevInput type
const computedPrice = computed({
  get() {
    return price.value === null
      ? { amount: '', currency: currency.value }
      : price.value
  },
  set(value) {
    price.value = value.amount === '' ? null : value
  },
})

// Computed property to handle null to empty string conversion to match RevInput type
const computedSku = computed({
  get() {
    return sku.value === null ? '' : sku.value
  },
  set(value) {
    sku.value = value
  },
})

const isButtonDisabled = computed(() => {
  if (priceToWinBackbox.value.amount === '0') {
    return true
  }

  return (
    Number(computedPrice.value.amount) >= Number(priceToWinBackbox.value.amount)
  )
})

function handlePriceUpdate(value: InputValue) {
  if (value) {
    computedPrice.value = {
      amount: value.toString() ?? '0',
      currency: currency.value,
    }
  }
}

/**
 * As we only want to display the tooltip if the seller have the backbox, we're checking :
 * - if the button is disabled (value >= priceToWinBackbox),
 * - there is a priceToWinBackbox.
 */
function handleTooltipVisibility(show: () => Promise<void>, hide: () => void) {
  return isButtonDisabled.value && priceToWinBackbox.value.amount !== '0'
    ? show()
    : hide()
}
</script>
